/**
 * ShowroomModule - 店舗情報表示・絞り込み機能
 */
export default class ShowroomModule {
  constructor(options = {}) {
    this.csvUrl = options.csvUrl || 'https://cdn.shopify.com/s/files/1/0792/8856/6074/files/shops.csv';
    this.regionsUrl = options.regionsUrl || 'https://cdn.shopify.com/s/files/1/0792/8856/6074/files/regions.csv';
    this.containerSelector = options.containerSelector || '#showroom-container';
    this.filterContainerSelector = options.filterContainerSelector || '.p-tab-block';
    this.filterSelector = options.filterSelector || 'input[name="showroom_filter"]';

    this.shopsData = [];
    this.regionsData = [];
    this.filteredData = [];

    // DOM要素はinit()で取得
    this.$containerElm = null;
    this.$filterContainerElm = null;
    this.$filterElms = [];

    // メソッドのバインド
    this.boundOnFilterChange = this.onFilterChange.bind(this);
  }

  /**
   * 初期化処理
   */
  async init() {
    // DOM要素を初期化時に取得
    this.$containerElm = document.querySelector(this.containerSelector);
    this.$filterContainerElm = document.querySelector(this.filterContainerSelector);

    if (!this.$containerElm || !this.$filterContainerElm) {
      console.error('Required containers not found:', {
        container: !!this.$containerElm,
        filterContainer: !!this.$filterContainerElm
      });
      return;
    }

    try {
      // 地域データを先に読み込み（フィルター生成に必要）
      await this.loadRegionsData();
      this.renderFilterTabs();

      // 店舗データを読み込み
      await this.loadShopsData();
      this.renderShops(this.shopsData);
      this.initializeFilters();
    } catch (error) {
      console.error('Failed to initialize ShowroomModule:', error);
      this.renderError();
    }
  }

  /**
   * 地域CSV読み込み・パース処理
   */
  async loadRegionsData() {
    const res = await fetch(this.regionsUrl);
    if (!res.ok) {
      throw new Error(`地域CSV読み込み失敗: ${res.status}`);
    }

    const csvText = await res.text();
    this.regionsData = this.parseCSV(csvText);
    // 表示順でソート
    this.regionsData.sort((a, b) => parseInt(a['表示順']) - parseInt(b['表示順']));
  }

  /**
   * 店舗CSV読み込み・パース処理
   */
  async loadShopsData() {
    const res = await fetch(this.csvUrl);
    if (!res.ok) {
      throw new Error(`店舗CSV読み込み失敗: ${res.status}`);
    }

    const csvText = await res.text();
    this.shopsData = this.parseCSV(csvText);
    this.filteredData = [...this.shopsData];
  }

  /**
   * CSVテキストをオブジェクト配列に変換
   */
  parseCSV(csvText) {
    const lines = csvText.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      throw new Error('CSV形式が不正です');
    }

    const headers = lines[0].split(',').map(header => header.trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);

      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim() || '';
        });

        // 地域データの場合は地方名、店舗データの場合は店舗名をチェック
        if (row['地方名'] || row['店舗名']) {
          rows.push(row);
        }
      } else {
        console.warn(`行${i}: カラム数不一致 values=${values.length}, headers=${headers.length}`);
      }
    }

    return rows;
  }

  /**
   * CSV行をパース（カンマ区切りでクォート対応）
   */
  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  }

  /**
   * フィルタータブ動的生成
   */
  renderFilterTabs() {
    if (!this.regionsData || this.regionsData.length === 0) {
      console.warn('地域データが見つかりません');
      return;
    }

    const tabsHTML = this.regionsData.map(region => `
      <li>
        <label class="c-tag">
          <input class="c-tag-input" type="checkbox" name="showroom_filter" value="${region['地方名']}">
          <span class="c-tag-text">${region['地方名']}</span>
        </label>
      </li>
    `).join('');

    this.$filterContainerElm.innerHTML = `<ul class="p-taglist">${tabsHTML}</ul>`;
  }

  /**
   * 店舗HTML動的生成
   */
  renderShops(shops) {
    if (!shops || shops.length === 0) {
      this.$containerElm.innerHTML = '<p class="no-shops">該当する店舗が見つかりませんでした。</p>';
      return;
    }

    const html = shops.map(shop => this.createShopHTML(shop)).join('');
    this.$containerElm.innerHTML = html;
  }

  /**
   * 単一店舗のHTMLを生成
   */
  createShopHTML(shop) {
    const baseFileName = shop['画像'] ? shop['画像'].replace(/\.(jpg|jpeg|png|webp)$/i, '') : '';
    const jpgPath = baseFileName ? `https://cdn.shopify.com/s/files/1/0792/8856/6074/files/${baseFileName}.jpg` : '';
    const webpFallback = baseFileName ? `https://cdn.shopify.com/s/files/1/0792/8856/6074/files/${baseFileName}.webp` : '';
    const tel = shop['電話番号'] ? `<li>TEL｜<a href="tel:${shop['電話番号']}">${shop['電話番号']}</a></li>` : '';
    const links = this.createLinksHTML(shop);

    return `
      <div class="p-showroom-inner" data-region="${shop['地方']}">
        <div class="p-showroom-info">
          <h2><span>${shop['都道府県']}</span>${shop['店舗名']}</h2>
          <p>${shop['概要']}</p>
          <ul>
            <li>${shop['住所']}</li>
            ${tel}
          </ul>
          ${links}
        </div>
        <figure class="p-showroom-img">
          <img src="${jpgPath}"
               alt="${shop['店舗名']}"
               loading="lazy"
               onerror="this.src='${webpFallback}'; this.onerror=null;">
        </figure>
      </div>
    `;
  }

  /**
   * リンク部分のHTMLを生成
   */
  createLinksHTML(shop) {
    const links = [];

    if (shop['Google Maps']) {
      links.push(`<a class="p-showroom-info-links-btn" target="_blank" href="${shop['Google Maps']}">Googleマップ</a>`);
    }
    if (shop['Instagram']) {
      links.push(`<a class="p-showroom-info-links-btn" target="_blank" href="${shop['Instagram']}">Instagram</a>`);
    }
    if (shop['公式サイト']) {
      links.push(`<a class="p-showroom-info-links-btn" target="_blank" href="${shop['公式サイト']}">公式サイト</a>`);
    }
    if (shop['オンラインストア']) {
      links.push(`<a class="p-showroom-info-links-btn" target="_blank" href="${shop['オンラインストア']}">オンラインストア</a>`);
    }

    return links.length > 0 ? `<div class="p-showroom-info-links">${links.join('')}</div>` : '';
  }

  /**
   * フィルター機能の初期化
   */
  initializeFilters() {
    // 動的に生成されたフィルター要素を取得
    this.$filterElms = document.querySelectorAll(this.filterSelector);
    this.$filterElms.forEach($filterElm => {
      $filterElm.addEventListener('change', this.boundOnFilterChange);
    });
  }

  /**
   * フィルター変更時の処理
   */
  onFilterChange() {
    const selectedRegions = Array.from(this.$filterElms)
      .filter($filterElm => $filterElm.checked)
      .map($filterElm => $filterElm.value);

    if (selectedRegions.length === 0) {
      this.filteredData = [...this.shopsData];
    } else {
      this.filteredData = this.shopsData.filter(shop =>
        selectedRegions.includes(shop['地方'])
      );
    }

    this.renderShops(this.filteredData);
  }

  /**
   * エラー表示
   */
  renderError() {
    this.$containerElm.innerHTML = '<p class="error">店舗情報の読み込みに失敗しました。しばらくしてからもう一度お試しください。</p>';
  }

  /**
   * クリーンアップ処理
   */
  destroy() {
    this.$filterElms.forEach($filterElm => {
      $filterElm.removeEventListener('change', this.boundOnFilterChange);
    });
  }
}

// より確実な初期化タイミング
function initShowroom() {
  const el = document.querySelector('#showroom-container');
  if (!el) return;
  const showroom = new ShowroomModule({
    csvUrl: el.dataset.csvUrl,
    regionsUrl: el.dataset.regionsUrl,
  });
  showroom.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShowroom);
} else {
  initShowroom();
}
